import React, { useCallback } from "react";
import { getDisplaySDKUrl } from "./constants";
import type { PostMessageLog } from "./types";

interface SDKDemoProps {
  sdkLoaded: boolean;
  sdkError: string | null;
  isLoading: boolean;
  sdk: any;
  sdkOrderResult: any;
  retryCount: number;
  onSDKOpen: () => void;
  onAddLog: (log: PostMessageLog) => void;
  onRetry: () => void;
}

export const SDKDemo: React.FC<SDKDemoProps> = ({
  sdkLoaded,
  sdkError,
  isLoading,
  sdk,
  sdkOrderResult,
  retryCount,
  onSDKOpen,
  onRetry,
}) => {
  const handleSDKOpen = useCallback(() => {
    if (!sdk) {
      console.error("SDK not loaded");
      return;
    }
    onSDKOpen();
  }, [sdk, onSDKOpen]);

  return (
    <section className="demo-section">
      <h2>Демонстрация SDK методов</h2>

      {/* Состояние загрузки */}
      {isLoading && (
        <div className="sdk-status loading">
          <div className="status-content">
            <div className="spinner"></div>
            <p>Загрузка Preorder SDK...</p>
            <small>Подключение к серверу SDK</small>
          </div>
        </div>
      )}

      {/* Состояние ошибки */}
      {sdkError && !isLoading && (
        <div className="sdk-status error">
          <div className="status-content">
            <div className="error-icon">⚠️</div>
            <h3>Ошибка загрузки SDK</h3>
            <p>{sdkError}</p>
            <button onClick={onRetry} className="retry-button">
              🔄 Попробовать снова {retryCount > 0 && `(${retryCount})`}
            </button>
            <details className="error-details">
              <summary>Техническая информация</summary>
              <pre>{sdkError}</pre>
            </details>
          </div>
        </div>
      )}

      {/* SDK загружен успешно */}
      {sdkLoaded && !isLoading && !sdkError && (
        <div className="sdk-demo">
          <div className="sdk-buttons">
            <button
              onClick={handleSDKOpen}
              className="sdk-demo-button primary"
              disabled={!sdk}
            >
              {sdkOrderResult && sdkOrderResult.id ? (
                <>
                  {sdkOrderResult.items?.length || 0}{" "}
                  {(() => {
                    const count = sdkOrderResult.items?.length || 0;
                    if (count === 1) return "позиция";
                    if (count >= 2 && count <= 4) return "позиции";
                    return "позиций";
                  })()}{" "}
                  на {sdkOrderResult.priceTotal || 0} ₽
                </>
              ) : (
                "Добавить предзаказ"
              )}
            </button>
          </div>

          {sdkOrderResult && (
            <div className="sdk-result">
              <h4>Заказ №{sdkOrderResult.number}:</h4>
              <pre>{JSON.stringify(sdkOrderResult, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {/* Информация о SDK */}
      <div className="sdk-info">
        <details>
          <summary>ℹ️ Информация о Preorder SDK</summary>
          <div className="info-content">
            <p>
              <strong>URL SDK:</strong> {getDisplaySDKUrl()}
            </p>
            <p>
              <strong>Статус:</strong>{" "}
              {isLoading
                ? "Загрузка..."
                : sdkError
                  ? "Ошибка"
                  : sdkLoaded
                    ? "Загружен"
                    : "Не загружен"}
            </p>
            {sdk && (
              <>
                <p>
                  <strong>Версия:</strong> {sdk.version}
                </p>
                <p>
                  <strong>Открыто:</strong> {sdk.isOpen ? "Да" : "Нет"}
                </p>
                <p>
                  <strong>Текущая тема:</strong>{" "}
                  {sdk.currentTheme
                    ? typeof sdk.currentTheme === "string"
                      ? sdk.currentTheme
                      : sdk.currentTheme.theme
                    : "не установлена"}
                </p>
              </>
            )}
          </div>
        </details>
      </div>
    </section>
  );
};
